/* global functionality, login, globalNavigate, views, showModalError, showModalConfirm */
import React, { useState, useEffect, useContext } from "react";
import BqnContext from "common/BqnContext";
import styled from "styled-components";
import Request from "common/Request";
import Modal from "common/Modal";
import Table from "common/graphs/Table/Table";
import { NumberCell, renderShapeCell } from "common/graphs/Table/TableRenders";
import help from "help/viewFlowPolicies.md";
import { handleWarnings, policiesBatchCommand } from "./api";
import { safeStr, getPoliciesInRules } from "common/api";
import ActionsContext from "common/ActionsContext";
import { ActionsRow } from "views/Status/Policies/Common";
import useProfile from "common/hooks/useProfile";
import View from "common/View";
import { expressAsDataSpeed } from "common/utils";

const doesNothing = () => { };

const returnView = "viewFlowPolicies";

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const FooterMessage = styled.div`
  color: red;
  ul {
    padding: 0 0 0 15px;
    margin: 0px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
      text-align: left;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  names = [],
  whenDone,
  rules = []
}) => {
  const { delete: itemsToDelete, notDelete } = names.reduce((acc, name) => {
    if (rules.includes(name)) {
      acc.notDelete.push(name);
    } else {
      acc.delete.push(name);
    }
    return acc;
  }, { delete: [], notDelete: [] });

  const handleApply = () => {
    const cmds = itemsToDelete.map((name) => `clear policy flow ${safeStr(name)}`);
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(handleWarnings)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError(error, ""));
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    itemsToDelete.length === 1
      ? `Remove "${itemsToDelete[0]}" policy?`
      : `Remove ${itemsToDelete.length} policies?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={names.length > 1 && names.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {itemsToDelete.length === 1 ? (
            <p>
              WARNING: All policy data will be lost and all subscribers assigned
              to this policy will be reassigned to a default flow policy
              following the BQN configured rules (normally default policy is
              "flow-default").
            </p>
          ) : null}
          {itemsToDelete.length > 1 ? (
            <>
              {itemsToDelete.length < 100 ? (
                <ul>
                  {itemsToDelete.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                WARNING: All policies data will be lost and all subscribers
                assigned to these policies will be reassigned to a default
                flow policy following the BQN configured rules (normally
                default policy is "flow-default").
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      footerMessage={
        notDelete.length > 0 ? (
          <FooterMessage>
            <p>These policies cannot be deleted, because they are part of a rule.</p>
            <ul>
              {notDelete.map((item, i) => (
                <li key={i}>
                  {item}
                </li>
              ))}
            </ul>
          </FooterMessage>
        ) : null
      }
      onApply={handleApply}
    />
  );
};

function onAddPolicy() {
  globalNavigate("viewPolicyFlowEdit", { returnView });
}

export const convertPolicyFlowStatisticsResponse = (response) => {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const headerAttrs = header.trim(/\s/).split(/\s+/);
  const newRows = rows.map((row) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce((acc, attr, index) => {
      acc[headerAttrs[index]] = attr;
      return acc;
    }, {});
    return newEntry;
  });
  return { response: newRows };
};

export const PoliciesList = () => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [request, setRequest] = useState(null);

  const doRequest = (mode, bqnId) => {
    return ifCl
      .run(policiesBatchCommand(mode, bqnId), "batch_exmode")
      .then((response) => {
        const [policiesResponse, configResponse] = response.split(batchExModeSeparator);
        const policies = convertPolicyFlowStatisticsResponse(policiesResponse);
        const rules = getPoliciesInRules(configResponse, "flow");
        return { policies, rules };
      })
      .catch((error) => console.log(error));
  };

  const doLoad = (mode, bqnId) => {
    setRequest(doRequest(mode, bqnId));
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the command would not carry its option, and the response would be that of
  // the whole cluster. The effect runs again when it does.
  useEffect(
    () => (bqnId === undefined ? undefined : doLoad(mode, bqnId)),
    [mode, bqnId]
  );

  if (request === null) {
    return null;
  }

  return (
    <View
      title="Flow Policies"
      mdHelp={help}
      refresh={() => doLoad(mode, bqnId)}
      options={[{ label: "Add Policy", onClick: onAddPolicy }]}
    >
      <div className="body table-responsive">
        <ActionsRow doCreatePolicy={onAddPolicy} />
        <Request during={request}>
          {(result) => (
            <ShowPolicies
              {...result}
              reload={() => doLoad(mode, bqnId)}
              returnView={returnView}
            />
          )}
        </Request>
      </div>
    </View>
  );
};

/*****************************************************
 * Policies Table
 * ****************************************************/

const TableContainer = styled.div`
  width: 100%;
`;

const openThroughputPerPolicy = (
  policyType = "flow",
  directions = "downSpeed"
) => {
  views.doKeep(returnView);
  globalNavigate("viewThroughputPerPolicy", {
    policyType,
    directions,
    returnView,
  });
};

const renderShapeCellCensored = (value, hideRates) => {
  if (hideRates) {
    return "n/a";
  }
  if (value === null || value === "no" || value === false) {
    return "no";
  }
  if (String(value).endsWith("%")) {
    return value;
  }
  return expressAsDataSpeed(parseFloat(value));
};


const getColumns = ({
  profile: { hideRates = false, dpiEnabled = false },
  reload,
  returnView,
  compressionEnabled,
  setDeleteSelected,
  setShowDeleteModal,
}) => {
  return [
    {
      label: "NAME",
      id: "NAME",
      numeric: false,
      cellClassName: "hyperlink-text",
      onClick: hideRates ? null : (value) => doEditPolicy(value, returnView),
    },
    {
      label: "FLOWS",
      id: "FLOWS",
      cellClassName: "hyperlink-text",
      numeric: true,
      renderCell: (value) => <NumberCell value={value} precision={0} />,
      onClick: (value, row) => {
        globalNavigate("viewFlowsPerSubs", {
          returnView,
          policy: row["NAME"],
        });
      },
    },
    {
      label: "MBYTES-DN",
      id: "MBYTES-DOWNLINK",
      cellClassName: "hyperlink-text",
      numeric: true,
      renderCell: (value) => <NumberCell value={value} precision={3} />,
      onClick: () => {
        openThroughputPerPolicy("flow", "downSpeed");
      },
    },
    {
      label: "MBYTES-UP",
      id: "MBYTES-UPLINK",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => <NumberCell value={value} precision={3} />,
      onClick: () => {
        openThroughputPerPolicy("flow", "upSpeed");
      },
    },
    {
      label: "BLOCK",
      id: "BCK",
      numeric: false,
      renderCell: (value) => (hideRates ? "n/a" : value),
      hidden: !dpiEnabled,
    },
    ...(login.isPlatform("cambium")
      ? [
        {
          label: "TCPO",
          id: "OPT",
          numeric: false,
          renderCell: (value) => (value === "no" ? "no" : "yes"),
        },
      ]
      : [
        {
          label: "TCPO",
          id: "OPT",
          numeric: true,
          renderCell: (value) => {
            if (value === "yes" || value === "no") {
              return value;
            }
            return <NumberCell value={value} precision={2} percent={true} />;
          },
        },
      ]),
    {
      label: "COMPRESS",
      id: "COM",
      numeric: false,
      hidden: !compressionEnabled,
    },
    {
      label: "SHAPE-DN",
      id: "SHPPSD",
      numeric: true,
      renderCell: renderShapeCell,
    },
    {
      label: "SHAPE-UP",
      id: "SHPPSU",
      numeric: true,
      renderCell: renderShapeCell,
    },
    {
      label: "SHAPE-FLOW-DN",
      id: "SHPPFD",
      numeric: true,
      renderCell: (value) => renderShapeCellCensored(value, hideRates),
      parse: (value) => renderShapeCellCensored(value, hideRates),
    },
    {
      label: "SHAPE-FLOW-UP",
      id: "SHPPFU",
      numeric: true,
      renderCell: (value) => renderShapeCellCensored(value, hideRates),
      parse: (value) => renderShapeCellCensored(value, hideRates),
    },
    {
      label: "PRIO",
      id: "SHPPRI",
      numeric: true,
      renderCell: (value) => {
        if (hideRates) {
          return "n/a";
        }
        return <NumberCell value={value} precision={0} />;
      },
    },
    {
      label: "DSCP(d/u)",
      id: "DSCPDU",
      numeric: false,
    },
    {
      label: "DROP",
      id: "DIT",
      numeric: false,
    },
    {
      label: "SKIP-QUOTA",
      id: "QTA",
      numeric: false,
      renderCell: (value) => {
        if (hideRates) {
          return "n/a";
        }
        return value === "yes" ? "no" : "yes";
      },
    },
    {
      label: "SKIP-SUB-BLOCK",
      id: "SBK",
      numeric: false,
      renderCell: (value) =>
        hideRates
          ? "n/a"
          : value === "yes"
            ? "no"
            : value === "no"
              ? "yes"
              : value,
    },
    {
      label: "SKIP-RATE",
      id: "SRS",
      numeric: false,
      renderCell: (value) => (hideRates ? "n/a" : value),
    },
    {
      label: "SKIP-SUBGR",
      id: "SRG",
      numeric: false,
      renderCell: (value) => (hideRates ? "n/a" : value),
    },
    ...(!hideRates
      ? [
        {
          label: "ACTIONS",
          id: "ACTIONS",
          actions: [
            {
              label: "Edit",
              icon: "edit",
              onClick: (value) => {
                doEditPolicy(value["NAME"], returnView);
              },
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: (value) => {
                setDeleteSelected([value["NAME"]]);
                setShowDeleteModal(true);
              },
              hide: (value) => value["NAME"] === "flow-default",
            },
          ],
        },
      ]
      : []),
  ];
};

export const ShowPolicies = ({
  policies,
  rules = [],
  reload = doesNothing,
  returnView,
}) => {
  const response = policies.response;
  let profile = useProfile();
  const { clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  if (profile === null) {
    return null;
  }

  const columns = getColumns({
    profile,
    reload,
    returnView,
    compressionEnabled: functionality.isEnabled("compression"),
    setDeleteSelected,
    setShowDeleteModal,
  });

  const newResponse = response.map((row, index) => {
    return Object.entries(row).reduce((acc, [key, value]) => {
      if (key === "DSCPD") {
        acc['DSCPDU'] = `${row["DSCPD"]}/${row["DSCPU"]}`;
      } else if (key !== "DSCPU") {
        acc[key] = value;
      }
      return acc;
    }, { id: row.id !== undefined ? row.id : index })
  });

  return (
    <>
      <TableContainer>
        <Table
          isSelectable={true}
          isRowSelectable={false}
          data={newResponse}
          sort={true}
          columns={columns}
          search={true}
          csvPrefix="flow-policies"
          rowsPerPageNr={50}
          selected={selected}
          setSelected={(value) => {
            setSelected(value);
          }}
          deleteSelected={(values) => {
            const selectedNames = newResponse
              .filter((row) => values.includes(row.id))
              .map((row) => row["NAME"]);
            setDeleteSelected(selectedNames);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        names={deleteSelected}
        whenDone={() => {
          setSelected([]);
          reload();
        }}
        rules={rules}
      />
    </>
  );
};

const doEditPolicy = (policy, returnView) => {
  globalNavigate("viewPolicyFlowEdit", {
    returnView,
    policy,
    tab: 2,
  });
};
